import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Routes, useLocation, Route, matchRoutes } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import lodash from 'lodash';
import clsx from 'clsx';
import { Box } from '@mui/material';
import { MaterialsRouter } from 'ui/modules/materials';
import { SetupRouter } from 'ui/modules/setup';
import { PurchasingRouter } from 'ui/modules/purchasing';
import { SalesRouter } from 'ui/modules/sales';
import { ReportsRouter } from 'ui/modules/reports';
import { HomeRouter } from 'ui/modules/home';
import { AccountingRouter } from 'ui/modules/accounting';
import { CommerceRouter } from 'ui/modules/commerce';
import { IntegrationsRouter } from 'ui/modules/integrations';
import { NotificationsRouter } from 'ui/modules/notifications';
import { RemoteSupportRouter } from 'ui/modules/remotesupport';
import { FeedbackRouter } from 'ui/modules/feedback/navigation';

import { Routes as AppRoutes } from '../../navigation';
import { useAppHomeStyles } from './styled';

import { getActiveUser } from 'services/user';

import FBODrawer from 'ui/theme/components/FBODrawer/FBODrawer';
import { FBOAppBar } from 'ui/theme/components/FBOAppBar';
import { datadogRum } from '@datadog/browser-rum';
import { isProd } from 'helpers';
export interface AppHomeCmp extends React.FunctionComponent {
  route: string;
}

const AppHome: AppHomeCmp = (props: any) => {
  const classes = useAppHomeStyles(props);
  const location = useLocation();

  const activeUser = useSelector(getActiveUser);
  const ldClient = useLDClient();

  const [drawerExpanded, setDrawerExpanded] = React.useState(true);
  const [drawerExpandedPersistent, setDrawerExpandedPersistent] =
    React.useState(true);

  const handleDrawerExpand = useCallback(
    (expanded: boolean, persistent: boolean) => {
      setDrawerExpanded(expanded);
      if (persistent) {
        setDrawerExpandedPersistent(expanded);
      }
    },
    []
  );

  useEffect(() => {
    if (ldClient && activeUser.userGlobalId && activeUser.user) {
      const key = lodash.get(activeUser, 'userGlobalId', '000') as string;
      const email = lodash.get(activeUser, 'user.email', 'n/a');
      const firstName = lodash.get(activeUser, 'user.firstName', 'n/a');
      const lastName = lodash.get(activeUser, 'user.lastName', 'n/a');
      const name = `${firstName} ${lastName}`;

      ldClient.identify({
        kind: 'user',
        key,
        email,
        name,
      });
    }
  }, [activeUser]);

  useEffect(() => {
    if (
      isProd() &&
      window.Appcues &&
      activeUser.user &&
      activeUser.userGlobalId
    ) {
      try {
        const dateCreatedStr = activeUser?.user?.dateCreated;
        const createdAt = dateCreatedStr
          ? Math.floor(new Date(dateCreatedStr).getTime() / 1000)
          : undefined;

        window.Appcues.identify(String(activeUser.userGlobalId), {
          firstName: activeUser.user.firstName || activeUser.user.name,
          email: activeUser.user.email,
          accountId: activeUser.tenant,
          language: 'en',
          timezone: activeUser.user.timezone,
          createdAt: createdAt,
        });
        window.Appcues.page();
      } catch (error) {
        console.error('Appcues tracking failed:', error);
      }
    }
  }, [location.pathname, activeUser]);

  // All routes are always available - access control handled within each router
  const routes = useMemo(
    () => [
      { path: HomeRouter.route, element: <HomeRouter /> },
      { path: NotificationsRouter.route, element: <NotificationsRouter /> },
      { path: RemoteSupportRouter.route, element: <RemoteSupportRouter /> },
      { path: FeedbackRouter.route, element: <FeedbackRouter /> },
      { path: MaterialsRouter.route, element: <MaterialsRouter /> },
      { path: SetupRouter.route, element: <SetupRouter /> },
      { path: SalesRouter.route, element: <SalesRouter /> },
      { path: PurchasingRouter.route, element: <PurchasingRouter /> },
      { path: ReportsRouter.route, element: <ReportsRouter /> },
      { path: AccountingRouter.route, element: <AccountingRouter /> },
      { path: CommerceRouter.route, element: <CommerceRouter /> },
      { path: IntegrationsRouter.route, element: <IntegrationsRouter /> },
    ],
    []
  );

  // Track every route change with useLocation API
  const routerLocation = useLocation();

  // React router instrumentation
  // https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#react-router-instrumentation
  const computeViewName = (routeMatches: string | any[]) => {
    let viewName = '';
    for (let index = 0; index < routeMatches.length; index++) {
      const routeMatch = routeMatches[index];
      const path = routeMatch.route.path;
      // Skip pathless routes
      if (!path) {
        continue;
      }

      if (path.startsWith('/')) {
        // Handle absolute child route paths
        viewName = path;
      } else {
        // Handle route paths ending with "/"
        viewName += viewName.endsWith('/') ? path : `/${path}`;
      }
    }

    return viewName || '/';
  };

  useEffect(() => {
    const routeMatches = matchRoutes(routes, routerLocation.pathname);

    const viewName = routeMatches && computeViewName(routeMatches);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  }, [routerLocation.pathname]);

  return (
    <Box
      className={clsx('redesign', {
        [classes.homeWrapper]: false,
        'home-page-layout': true,
      })}
    >
      <FBODrawer
        drawerExpanded={drawerExpanded || drawerExpandedPersistent}
        drawerPersistent={drawerExpandedPersistent}
        onDrawerExpand={handleDrawerExpand}
        activePathName={location.pathname}
      />

      <Box
        className={clsx('main-page-layout', {
          [classes.contentWrapper]: false,
        })}
      >
        <FBOAppBar />
        <Routes>
          {routes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
        </Routes>
      </Box>
    </Box>
  );
};

AppHome.route = AppRoutes.AppHome;

export default AppHome;
